export type Config = {
  ENV: string
  MSAL_AUTHORITY: string
  MSAL_CLIENT_ID: string
  MSAL_REDIRECT_URI: string
  MSAL_LOOM_APP_URI: string
  API_HOST_URI: string
  DEBUG_AUTH: string
  GOOGLE_ANALYTICS_TAG_ID: string
  MICROSOFT_CLARITY_ID: string
  ELASTIC_APM_ENDPOINT: string
  ELASTIC_APM_ACTIVE: boolean
  ELASTIC_APM_DISTRIBUTED_TRACING_ACTIVE: boolean
  ELASTIC_APM_DISABLE_INSTRUMENTATIONS: InstrumentationTypes[]
  ELASTIC_APM_LOG_LEVEL: string
}

let singletonService: ConfigService | null = null

class ConfigService {
  private config: Config | null

  constructor() {
    const isDevOrTest = ['development', 'test', 'playwright'].includes(import.meta.env.MODE)
    const useDeployedConfig = !isDevOrTest

    // Azure Static Apps will inject the config as a global variable at request time
    if (useDeployedConfig) {
      if (!(window as any).NZM_CONFIG) {
        this.config = null
        throw new Error('Config not configured in deployed environment')
      }
      this.config = (window as any).NZM_CONFIG as Config
    } else {
      this.config = {
        ENV: import.meta.env.VITE_ENV ?? 'local',
        MSAL_AUTHORITY: import.meta.env.VITE_MSAL_AUTHORITY ?? '',
        MSAL_CLIENT_ID: import.meta.env.VITE_MSAL_CLIENT_ID ?? '',
        MSAL_REDIRECT_URI: import.meta.env.VITE_MSAL_REDIRECT_URI ?? '',
        MSAL_LOOM_APP_URI: import.meta.env.VITE_MSAL_LOOM_APP_URI ?? '',
        API_HOST_URI: import.meta.env.VITE_API_HOST_URI ?? '',
        DEBUG_AUTH: import.meta.env.VITE_DEBUG_AUTH ?? '',
        GOOGLE_ANALYTICS_TAG_ID: import.meta.env.VITE_GOOGLE_ANALYTICS_TAG_ID ?? '',
        MICROSOFT_CLARITY_ID: import.meta.env.VITE_MICROSOFT_CLARITY_ID ?? '',
        ELASTIC_APM_ENDPOINT: import.meta.env.VITE_ELASTIC_APM_ENDPOINT ?? '',
        ELASTIC_APM_ACTIVE: import.meta.env.VITE_ELASTIC_APM_ACTIVE === 'true',
        ELASTIC_APM_DISTRIBUTED_TRACING_ACTIVE:
          import.meta.env.VITE_ELASTIC_APM_DISTRIBUTED_TRACING_ACTIVE === 'true',
        ELASTIC_APM_DISABLE_INSTRUMENTATIONS:
          import.meta.env.VITE_ELASTIC_APM_DISABLE_INSTRUMENTATIONS ?? [],
        ELASTIC_APM_LOG_LEVEL: import.meta.env.VITE_ELASTIC_APM_LOG_LEVEL ?? 'debug',
      }
    }
  }

  /**
   * Retrieves the value associated with the specified configuration key.
   *
   * @typeParam K - The key of the configuration property to retrieve.
   * @param key - The key of the configuration property.
   * @returns The value corresponding to the specified key in the configuration.
   * @throws {Error} If the configuration has not been set up.
   *
   * @example
   * const service = configService();
   * const env = service.get('ENV'); // env is inferred as string
   * const apmActive = service.get('ELASTIC_APM_ACTIVE'); // apmActive is inferred as boolean
   * const logLevel = service.get('ELASTIC_APM_LOG_LEVEL'); // logLevel is inferred as string
   * const disabledInstrumentations = service.get('ELASTIC_APM_DISABLE_INSTRUMENTATIONS'); // disabledInstrumentations is inferred as InstrumentationTypes[]
   */
  public get<K extends keyof Config>(key: K): Config[K] {
    if (!this.config) {
      throw new Error('Config not configured')
    }
    return this.config[key]
  }
}

export function configService(): ConfigService {
  if (!singletonService) {
    singletonService = new ConfigService()
  }

  return singletonService
}
